// Global Instructions Rule Applied!
//
// In-memory cache of mobile browse-list state, keyed by module + serialized filters. Lets a
// list survive an unmount within a session so returning from a detail screen is instant
// (the felt-slow "go in, come back, wait for it to reload again" case) and restores scroll
// position. Deliberately NOT a Redux slice and NOT the module slices: list rows in a module
// slice would shadow `useRecordDetail`'s cold-RPC fetch, and a dedicated slice would add
// serialization churn on every page append for purely transient view state. A cold PWA
// relaunch intentionally starts fresh (unlike recents). LRU-capped so it can't grow
// unbounded across many filter permutations.

export interface CachedList {
  rows: any[]
  page: number
  hasMore: boolean
  totalCount: number
  /** Last scroll offset of the shell's scroll container for this list. */
  scrollTop: number
}

const MAX_KEYS = 8
const cache = new Map<string, CachedList>()

/** Cache key for a module + its serialized extra filters (incl. any pipeline scope). */
export function listCacheKey(module: string, serializedFilters: string): string {
  return `${module}|${serializedFilters}`
}

/** Read a cached list (and bump it to most-recently-used). */
export function getCachedList(key: string): CachedList | undefined {
  const v = cache.get(key)
  if (v) {
    cache.delete(key)
    cache.set(key, v)
  }
  return v
}

/** Write a cached list, evicting the least-recently-used beyond the cap. */
export function setCachedList(key: string, value: CachedList): void {
  if (cache.has(key)) cache.delete(key)
  cache.set(key, value)
  while (cache.size > MAX_KEYS) {
    const oldest = cache.keys().next().value
    if (oldest === undefined) break
    cache.delete(oldest)
  }
}

/** Drop a cached list (used on explicit reload / pull-to-refresh). */
export function clearCachedList(key: string): void {
  cache.delete(key)
}

/** Update just the scroll offset for an existing cached list (no-op if absent). */
export function setCachedScrollTop(key: string, scrollTop: number): void {
  const v = cache.get(key)
  if (v) v.scrollTop = scrollTop
}
